import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useToday, useTrainingPlan, useTrialStatus } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { LockedWorkout } from '../../components/LockedWorkout';
import { TrialBanner } from '../../components/TrialBanner';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDay } from '../../lib/format';
import { lockedWorkoutIds } from '../../lib/trial';
import { planWorkouts } from '../../lib/program';
import { EmptyState, SkeletonCard, SkeletonList, WorkoutCard } from '../../ui';

/**
 * Plan tjedna nad `GET /v1/training/plan`.
 *
 * Ruta vraća TJEDAN sa sedam dana (`weekIndex`, `days[]`), a ne popis treninga.
 * Portal je dosad čitao izmišljeni oblik `{ programName, week, totalWeeks,
 * workouts[] }` koji postoji samo u mocku; dani odmora se sad izvode iz
 * rasporeda umjesto da se prešućuju.
 */
export function Training() {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const query = useTrainingPlan();
  const today = useToday();
  const trial = useTrialStatus();

  const workouts = useMemo(() => planWorkouts(query.data), [query.data]);

  const locked = useMemo(
    () =>
      lockedWorkoutIds(
        workouts.map((w) => ({ id: w.workoutId, scheduledFor: w.scheduledOn })),
        trial.data?.lockedFrom?.workoutId,
      ),
    [workouts, trial.data],
  );

  return (
    <>
      <PageHeader title={t('training.title')} />

      <TrialBanner status={trial.data} className="mb-5" />

      {/* Trening dana ima vlastito stanje: zaključan trening nudi kupnju, ne prazan ekran. */}
      <section className="mb-6">
        <QueryBoundary query={today} skeleton={<SkeletonCard />}>
          {(data) => {
            if (data.reason === 'locked') {
              return (
                <LockedWorkout
                  name={trial.data?.lockedFrom?.name ?? null}
                  trialCount={data.lock?.trialCount ?? null}
                />
              );
            }
            if (data.reason === 'no_program') {
              return (
                <EmptyState
                  message={t('empty.noProgramChosen')}
                  actionLabel={t('empty.chooseProgram')}
                  onAction={() => void navigate('/pretplata')}
                />
              );
            }
            if (!data.workout) {
              return <EmptyState message={t('empty.noWorkoutToday')} />;
            }
            return (
              <WorkoutCard
                title={data.workout.name}
                to={`/trening/${data.workout.workoutId}`}
                exercisesLabel={t('dash.exercisesCount', { n: data.workout.exercises.length })}
                {...(data.workout.estimatedMinutes
                  ? { durationLabel: t('dash.minutes', { n: data.workout.estimatedMinutes }) }
                  : {})}
                weekDayLabel={t('dash.today')}
                statusLabel={t('portal.startWorkout')}
              />
            );
          }}
        </QueryBoundary>
      </section>

      <h2 className="eyebrow mb-3">{t('training.plan')}</h2>
      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={4} />}
        isEmpty={() => workouts.length === 0}
        empty={{
          message: t('empty.noWorkoutsPlanned'),
          title: t('training.plan'),
        }}
      >
        {(data) => (
          <>
            {data.programName && (
              <p className="mb-4 text-[13px] text-ink-muted">
                {data.programName} · {t('trainer.weeks', { n: data.weeksTotal })} ·{' '}
                {data.weekIndex + 1}/{data.weeksTotal}
              </p>
            )}
            <ul className="flex flex-col gap-3">
              {workouts.map((w) => (
                <li key={w.workoutId}>
                  <WorkoutCard
                    title={w.name}
                    to={`/trening/${w.workoutId}`}
                    weekDayLabel={formatDay(w.scheduledOn, locale)}
                    exercisesLabel={w.typeLabel ? t(w.typeLabel) : ''}
                    status={w.status}
                    locked={locked.has(w.workoutId)}
                    lockedLabel={t('trial.lockedBadge')}
                    unlockLabel={t('trial.unlock')}
                    onUnlock={() => void navigate('/pretplata')}
                    statusLabel={
                      w.status === 'done'
                        ? t('portal.workoutComplete')
                        : w.status === 'missed'
                          ? t('trainer.flagMissed', { n: 1 })
                          : t('portal.startWorkout')
                    }
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </QueryBoundary>
    </>
  );
}
